import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { instructorApi, uploadInstructorFile } from "../../../services/instructorApi";
import OnboardingStepFrame from "../../../components/instructor/OnboardingStepFrame";

const inputClass =
  "mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500";

export default function CredentialsStep() {
  const { instructor, refreshInstructor } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState([]);
  const [form, setForm] = useState({ name: "", issuer: "", year_earned: "" });
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCredentials(instructor?.credentials || []);
  }, [instructor]);

  async function addCredential(e) {
    e.preventDefault();
    setError("");
    if (!file) {
      setError("Please upload a certificate document (PDF, JPG, or PNG).");
      return;
    }
    setSaving(true);
    try {
      const asset = await uploadInstructorFile(file, "certificate");
      await instructorApi.createCredential({
        name: form.name,
        issuer: form.issuer,
        year_earned: form.year_earned ? Number(form.year_earned) : null,
        document_asset_id: asset.id,
      });
      setForm({ name: "", issuer: "", year_earned: "" });
      setFile(null);
      const input = document.getElementById("cert-file-input");
      if (input) input.value = "";
      await refreshInstructor();
    } catch (err) {
      setError(err.message || "Could not upload certificate");
    } finally {
      setSaving(false);
    }
  }

  async function removeCredential(id) {
    try {
      await instructorApi.deleteCredential(id);
      await refreshInstructor();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <OnboardingStepFrame
      title="Credentials that build trust"
      subtitle="Students trust verified teachers. Add at least one certification with a document upload (PDF or image required)."
    >
      {error && <div className="mb-4 rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      <div className="space-y-3 mb-6">
        {credentials.map((c) => (
          <div key={c.id} className="bg-white border border-gray-100 rounded-xl p-4 flex justify-between gap-3">
            <div>
              <p className="font-medium text-gray-900">{c.name}</p>
              <p className="text-sm text-gray-500">
                {[c.issuer, c.year_earned].filter(Boolean).join(" · ")}
                {c.has_document || c.document_asset_id ? " · Document uploaded" : " · No document"}
              </p>
            </div>
            <button type="button" onClick={() => removeCredential(c.id)} className="text-sm text-red-600 hover:underline">
              Remove
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={addCredential} className="bg-white border border-gray-100 rounded-2xl p-6 space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Certification name</span>
          <input className={inputClass} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Issuer</span>
            <input className={inputClass} value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Year</span>
            <input
              type="number"
              className={inputClass}
              value={form.year_earned}
              onChange={(e) => setForm({ ...form, year_earned: e.target.value })}
            />
          </label>
        </div>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Certificate file (required — PDF or image)</span>
          <input
            id="cert-file-input"
            type="file"
            required
            accept=".pdf,image/jpeg,image/png,image/webp,image/jpg"
            className="mt-1 block w-full text-sm"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {file && <p className="mt-1 text-xs text-gray-500">Selected: {file.name}</p>}
        </label>
        <button type="submit" disabled={saving} className="rounded-lg border border-emerald-200 text-emerald-800 px-4 py-2 text-sm font-medium hover:bg-emerald-50 disabled:opacity-60">
          {saving ? "Uploading…" : "Add certification"}
        </button>
      </form>

      <div className="flex justify-between mt-6">
        <Link to="/instructor/onboarding/expertise" className="text-sm text-gray-600 hover:underline">
          Back
        </Link>
        <button
          type="button"
          onClick={() => navigate("/instructor/onboarding/media")}
          className="rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-emerald-700"
        >
          Continue
        </button>
      </div>
    </OnboardingStepFrame>
  );
}
